import { matchPath } from 'react-router-dom'

import { DEFAULT_ADHP_INFO_TAB, isAdhpInfoTab } from '@/presentation/modules/AdhpInfo/utils/adhpInfoNavigation'
import { RouterPaths } from '@/presentation/router/RouterPathsMapper'

export type ScreenMode = 'new' | 'edit'

export interface ScreenMatch {
  /** Nome estável e de cardinalidade fechada da tela (sem ids dinâmicos). */
  screen: string
  /** Presente apenas nas rotas que têm dualidade criação/edição. */
  mode?: ScreenMode
}

interface ScreenRoute {
  pattern: string
  /** `null` = rota conhecida que não representa uma tela (redirect, rota sem element). */
  screen: string | null
  mode?: ScreenMode
  /** Para rotas cujo estado vive na query string (ex. `/adhp-info?tab=`). */
  resolveScreen?: (search: string) => string
}

/**
 * `RouterPaths` usa `:id` em mais de um nível de aninhamento. Renomeamos os params ao compor
 * os padrões para que cada segmento dinâmico tenha um nome próprio.
 */
const AIRCRAFT_EDIT_ROOT = RouterPaths.AIRCRAFT_EDIT.replace(':id', ':aircraftId')
const WB_PROFILES_EDIT_SEGMENT = RouterPaths.WB_PROFILES_EDIT.replace(':id', ':profileId')

const WB_PROFILE_SCREEN_PREFIX = 'aircraft/wb-profile'

/** Passos de um perfil de P&B, relativos à raiz do perfil. */
const WB_PROFILE_STEPS: { segment: string; screen: string }[] = [
  { segment: RouterPaths.WB_CREATION_MODE, screen: `${WB_PROFILE_SCREEN_PREFIX}/creation-mode` },
  { segment: RouterPaths.USE_ANOTHER_TEMPLATE, screen: `${WB_PROFILE_SCREEN_PREFIX}/use-another-template` },
  { segment: RouterPaths.WB_CHOOSE_TEMPLATE, screen: `${WB_PROFILE_SCREEN_PREFIX}/choose-template` },
  { segment: RouterPaths.WB_UNITS, screen: `${WB_PROFILE_SCREEN_PREFIX}/wb-units` },
  { segment: `${RouterPaths.WB_STEPS}/${RouterPaths.WB_MAC}`, screen: `${WB_PROFILE_SCREEN_PREFIX}/wb-mac` },
  {
    segment: `${RouterPaths.WB_STEPS}/${RouterPaths.WB_BASIC_EMPTY_WEIGHT}`,
    screen: `${WB_PROFILE_SCREEN_PREFIX}/basic-empty-weight`
  },
  { segment: `${RouterPaths.WB_STEPS}/${RouterPaths.WB_SEATS}`, screen: `${WB_PROFILE_SCREEN_PREFIX}/seats` },
  { segment: `${RouterPaths.WB_STEPS}/${RouterPaths.WB_CARGO}`, screen: `${WB_PROFILE_SCREEN_PREFIX}/cargo` },
  { segment: `${RouterPaths.WB_STEPS}/${RouterPaths.WB_TANK}`, screen: `${WB_PROFILE_SCREEN_PREFIX}/tank` },
  {
    segment: `${RouterPaths.WB_STEPS}/${RouterPaths.WB_MAXIMUM_WEIGHTS}`,
    screen: `${WB_PROFILE_SCREEN_PREFIX}/maximum-weights`
  },
  { segment: `${RouterPaths.WB_STEPS}/${RouterPaths.WB_ENVELOPE}`, screen: `${WB_PROFILE_SCREEN_PREFIX}/envelope` },
  // `wb-steps` tem `wb-mac` como rota index
  { segment: RouterPaths.WB_STEPS, screen: `${WB_PROFILE_SCREEN_PREFIX}/wb-mac` }
]

/**
 * Os blocos de perfil de P&B aparecem 4× no `DrawerRouter` (aeronave nova/existente ×
 * perfil novo/existente). Colapsamos para um único nome de tela por passo; o que interessa
 * analiticamente é o modo do **perfil**, exposto em `mode`.
 */
const buildWBProfileRoutes = (): ScreenRoute[] => {
  const aircraftRoots = [RouterPaths.AIRCRAFT_NEW, AIRCRAFT_EDIT_ROOT]
  const profileRoots: { segment: string; mode: ScreenMode }[] = [
    { segment: RouterPaths.WB_PROFILES_NEW, mode: 'new' },
    { segment: WB_PROFILES_EDIT_SEGMENT, mode: 'edit' }
  ]

  return aircraftRoots.flatMap((aircraftRoot) =>
    profileRoots.flatMap(({ segment: profileRoot, mode }) => [
      ...WB_PROFILE_STEPS.map(({ segment, screen }) => ({
        pattern: `${aircraftRoot}/${profileRoot}/${segment}`,
        screen,
        mode
      })),
      // Raiz do perfil sem passo: nem todos os blocos têm rota index, mas evita ficar sem evento.
      { pattern: `${aircraftRoot}/${profileRoot}`, screen: WB_PROFILE_SCREEN_PREFIX, mode }
    ])
  )
}

/**
 * Padrões ordenados do mais específico para o mais genérico — vence o primeiro match.
 * A ordem importa: `/weight-and-balance/manage-default-weights` também casaria com
 * `/weight-and-balance/:fillId`, então os literais vêm antes dos dinâmicos.
 */
const SCREEN_ROUTES: ScreenRoute[] = [
  { pattern: '/', screen: null },
  { pattern: RouterPaths.MAP, screen: 'map' },

  // Aeronave
  { pattern: `${RouterPaths.AIRCRAFT_NEW}/${RouterPaths.AIRCRAFT_REGISTER}`, screen: 'aircraft/register', mode: 'new' },
  { pattern: `${AIRCRAFT_EDIT_ROOT}/${RouterPaths.AIRCRAFT_REGISTER}`, screen: 'aircraft/register', mode: 'edit' },
  ...buildWBProfileRoutes(),
  { pattern: RouterPaths.AIRCRAFT_NEW, screen: 'aircraft/new' },
  { pattern: RouterPaths.AIRCRAFT, screen: 'aircraft' },

  // Peso e Balanceamento
  {
    pattern: `${RouterPaths.WEIGHT_AND_BALANCE}/${RouterPaths.DEFAULT_WEIGHTS_MANAGEMENT}`,
    screen: 'weight-and-balance/manage-default-weights'
  },
  {
    pattern: `${RouterPaths.WEIGHT_AND_BALANCE}/${RouterPaths.WEIGHT_AND_BALANCE_NEW}/${RouterPaths.WEIGHT_AND_BALANCE_CREATION_MODE}`,
    screen: 'weight-and-balance/creation-mode',
    mode: 'new'
  },
  {
    pattern: `${RouterPaths.WEIGHT_AND_BALANCE}/${RouterPaths.WEIGHT_AND_BALANCE_NEW}/${RouterPaths.WB_FLIGHT_SETUP}`,
    screen: 'weight-and-balance/flight-setup',
    mode: 'new'
  },
  {
    pattern: `${RouterPaths.WEIGHT_AND_BALANCE}/${RouterPaths.WEIGHT_AND_BALANCE_NEW}/${RouterPaths.WB_FLIGHT_FILLING}`,
    screen: 'weight-and-balance/flight-filling',
    mode: 'new'
  },
  // Sem rota index: não é uma tela, e sem esta entrada cairia no padrão `:fillId` abaixo.
  { pattern: `${RouterPaths.WEIGHT_AND_BALANCE}/${RouterPaths.WEIGHT_AND_BALANCE_NEW}`, screen: null },
  {
    pattern: `${RouterPaths.WEIGHT_AND_BALANCE}/:fillId/${RouterPaths.WB_FLIGHT_SETUP}`,
    screen: 'weight-and-balance/flight-setup',
    mode: 'edit'
  },
  {
    pattern: `${RouterPaths.WEIGHT_AND_BALANCE}/:fillId/${RouterPaths.WB_FLIGHT_FILLING}`,
    screen: 'weight-and-balance/flight-filling',
    mode: 'edit'
  },
  // Rota index da edição é o flight-setup
  { pattern: `${RouterPaths.WEIGHT_AND_BALANCE}/:fillId`, screen: 'weight-and-balance/flight-setup', mode: 'edit' },
  { pattern: RouterPaths.WEIGHT_AND_BALANCE, screen: 'weight-and-balance' },

  // Checklists. Os passos do builder vêm antes de `:id`, que casaria com `new` e roubaria o nome.
  { pattern: `${RouterPaths.CHECKLISTS}/${RouterPaths.CHECKLISTS_NEW_TEMPLATE}`, screen: 'checklists/new/template' },
  {
    pattern: `${RouterPaths.CHECKLISTS}/${RouterPaths.CHECKLISTS_NEW_OTHER_TEMPLATE}`,
    screen: 'checklists/new/other-template'
  },
  {
    pattern: `${RouterPaths.CHECKLISTS}/${RouterPaths.CHECKLISTS_NEW_EDIT}/:procedureId`,
    screen: 'checklists/items',
    mode: 'new'
  },
  {
    pattern: `${RouterPaths.CHECKLISTS}/${RouterPaths.CHECKLISTS_NEW_EDIT}`,
    screen: 'checklists/procedures',
    mode: 'new'
  },
  { pattern: `${RouterPaths.CHECKLISTS}/${RouterPaths.CHECKLISTS_NEW}`, screen: 'checklists/new/aircraft' },
  {
    pattern: `${RouterPaths.CHECKLISTS}/${RouterPaths.CHECKLISTS_EDIT_ITEMS}`,
    screen: 'checklists/items',
    mode: 'edit'
  },
  {
    pattern: `${RouterPaths.CHECKLISTS}/${RouterPaths.CHECKLISTS_EDIT}`,
    screen: 'checklists/procedures',
    mode: 'edit'
  },
  { pattern: `${RouterPaths.CHECKLISTS}/${RouterPaths.CHECKLISTS_IMPORT}`, screen: 'checklists/import' },
  { pattern: RouterPaths.CHECKLISTS, screen: 'checklists' },

  // Meus Conteúdos
  {
    pattern: `${RouterPaths.MY_CONTENT}/${RouterPaths.MY_CONTENT__USER_WAYPOINTS}/${RouterPaths.MY_CONTENT__USER_WAYPOINTS__CREATE}`,
    screen: 'my-content/user-waypoints/create'
  },
  {
    pattern: `${RouterPaths.MY_CONTENT}/${RouterPaths.MY_CONTENT__USER_WAYPOINTS}/${RouterPaths.MY_CONTENT__USER_WAYPOINTS__EDIT}`,
    screen: 'my-content/user-waypoints/edit'
  },
  {
    pattern: `${RouterPaths.MY_CONTENT}/${RouterPaths.MY_CONTENT__USER_WAYPOINTS}`,
    screen: 'my-content/user-waypoints'
  },
  {
    pattern: `${RouterPaths.MY_CONTENT}/${RouterPaths.MY_CONTENT__USER_CHARTS}/${RouterPaths.MY_CONTENT__USER_CHARTS__UPLOAD}`,
    screen: 'my-content/user-charts/upload'
  },
  {
    pattern: `${RouterPaths.MY_CONTENT}/${RouterPaths.MY_CONTENT__USER_CHARTS}/${RouterPaths.MY_CONTENT__USER_CHARTS__EDIT}`,
    screen: 'my-content/user-charts/edit'
  },
  { pattern: `${RouterPaths.MY_CONTENT}/${RouterPaths.MY_CONTENT__USER_CHARTS}`, screen: 'my-content/user-charts' },
  {
    pattern: `${RouterPaths.MY_CONTENT}/${RouterPaths.MY_CONTENT__USER_GEOMETRIES}/${RouterPaths.MY_CONTENT__USER_GEOMETRIES__UPLOAD}`,
    screen: 'my-content/user-geometries/upload'
  },
  {
    pattern: `${RouterPaths.MY_CONTENT}/${RouterPaths.MY_CONTENT__USER_GEOMETRIES}/${RouterPaths.MY_CONTENT__USER_GEOMETRIES__EDIT}`,
    screen: 'my-content/user-geometries/edit'
  },
  {
    pattern: `${RouterPaths.MY_CONTENT}/${RouterPaths.MY_CONTENT__USER_GEOMETRIES}`,
    screen: 'my-content/user-geometries'
  },
  // Redireciona imediatamente para a aba de pontos: quem emite é a rota de destino.
  { pattern: RouterPaths.MY_CONTENT, screen: null },

  // Configurações
  {
    pattern: `${RouterPaths.SETTINGS}/${RouterPaths.SETTINGS__ROUTE_WAYPOINT_INSERTION_MODE}`,
    screen: 'settings/route-waypoint-insertion-mode'
  },
  { pattern: `${RouterPaths.SETTINGS}/${RouterPaths.SETTINGS__DATA_AND_CHARTS}`, screen: 'settings/data-and-charts' },
  { pattern: RouterPaths.SETTINGS, screen: 'settings' },

  // Telas simples
  { pattern: RouterPaths.ROUTES_PANEL, screen: 'routes-panel' },
  { pattern: RouterPaths.VERTICAL_NAVIGATION, screen: 'vertical-navigation' },
  { pattern: RouterPaths.SAVED_ROUTES, screen: 'saved-routes' },
  { pattern: RouterPaths.PROCEDURE_CHARTS, screen: 'procedure-charts' },
  {
    pattern: RouterPaths.ADHP_INFO,
    screen: 'adhp-info',
    // O estado da tela vive na query string (`?code=&tab=`). O `code` fica de fora
    // do nome da tela por ser de cardinalidade alta.
    resolveScreen: (search) => {
      const tab = new URLSearchParams(search).get('tab')
      return `adhp-info/${isAdhpInfoTab(tab) ? tab : DEFAULT_ADHP_INFO_TAB}`
    }
  },
  { pattern: RouterPaths.MARKET, screen: 'market' },
  { pattern: RouterPaths.BETA, screen: 'beta-info' },
  { pattern: RouterPaths.MOBILE, screen: 'mobile' },
  { pattern: RouterPaths.NEWS, screen: 'news' }
]

const removeTrailingSlash = (pathname: string) =>
  pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname

/**
 * Traduz a location atual em um nome de tela estável para analytics.
 * Retorna `null` quando a rota não representa uma tela (redirects, rota desconhecida).
 */
export function resolveScreenName(pathname: string, search = ''): ScreenMatch | null {
  const normalizedPathname = removeTrailingSlash(pathname)

  const route = SCREEN_ROUTES.find(({ pattern }) => matchPath({ path: pattern, end: true }, normalizedPathname))

  if (!route?.screen) return null

  return {
    screen: route.resolveScreen ? route.resolveScreen(search) : route.screen,
    ...(route.mode ? { mode: route.mode } : {})
  }
}
